import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import {
  subscribeToNotifications,
  markAsRead,
  markAllAsRead,
} from '../services/notificationService'
import type { Notification } from '@/types'

interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  markNotificationAsRead: (notificationId: string) => Promise<void>
  markAllNotificationsAsRead: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const { user, isDemo } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user || isDemo) {
      setNotifications([])
      setLoading(false)
      return
    }

    setLoading(true)

    // Subscribe to real-time notifications
    const unsubscribe = subscribeToNotifications(user.uid, (newNotifications) => {
      setNotifications(newNotifications)
      setLoading(false)
    })

    return () => {
      unsubscribe()
    }
  }, [user, isDemo])

  const unreadCount = notifications.filter((n) => !n.read).length

  const markNotificationAsRead = useCallback(async (notificationId: string) => {
    await markAsRead(notificationId)
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    )
  }, [])

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!user) return
    await markAllAsRead(user.uid)
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }, [user])

  return {
    notifications,
    unreadCount,
    loading,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  }
}
